import { useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { Button, Card } from "@/components/ui";
import { LegalCheckbox } from "@/components/Legal";
import { api, ApiError } from "@/lib/api";
import { isNative } from "@/lib/native";
import { useLogout } from "@/lib/session";
import { LEGAL_DOCS, LEGAL_VERSION } from "@shared/legal";
import type { MeDTO } from "@shared/types";

/** This copy of the app has older documents than the server: fetch the new build and show it. */
async function loadLatestApp() {
  const registration = await navigator.serviceWorker?.getRegistration();
  await registration?.update().catch(() => {});
  window.location.reload();
}

/** Shown when the Terms of Service or Privacy Policy changed: parents review and accept to continue. */
export function AcceptTerms() {
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const logout = useLogout();

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      // The version on this screen, not the server's latest: the record must match what the parent was shown.
      qc.setQueryData(["me"], await api.post<MeDTO>("/legal/accept", { version: LEGAL_VERSION, acceptLegal: true }));
    } catch (err) {
      if (err instanceof ApiError && err.code === "legal_outdated") {
        if (isNative()) setError("These terms were just updated. Update the app to read and accept them, or accept them on the website.");
        else await loadLatestApp();
        return;
      }
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh bg-canvas">
      <div className="mx-auto flex min-h-dvh max-w-md flex-col px-5 pb-[calc(env(safe-area-inset-bottom)+24px)] pt-[calc(env(safe-area-inset-top)+28px)]">
        <div className="flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-9" />
          <span className="font-black tracking-tight">Meaningful Rewards</span>
        </div>
        <h1 className="mt-8 text-3xl font-black leading-tight tracking-tight">We've updated our terms</h1>
        <p className="mt-2 text-muted">Please review the latest versions. Your family's data stays the same.</p>
        <Card className="mt-6 space-y-2 p-1.5">
          {Object.values(LEGAL_DOCS).map((d) => (
            <Link key={d.path} to={d.path} target="_blank" className="flex items-center gap-3 rounded-2xl px-3 py-3 hover:bg-sunken">
              <FileText className="size-5 text-brand" />
              <span className="flex-1 font-bold">{d.title}</span>
              <span className="text-sm text-muted">Version {d.version}</span>
            </Link>
          ))}
        </Card>
        <div className="mt-6">
          <LegalCheckbox checked={accepted} onChange={setAccepted} />
        </div>
        {error && <p className="mt-3 text-sm font-semibold text-danger">{error}</p>}
        <div className="mt-auto grid gap-3 pt-8">
          <Button size="lg" block onClick={accept} disabled={!accepted} loading={busy}>
            Accept and continue
          </Button>
          <a href="/api/family/export" download>
            <Button size="lg" block variant="secondary">
              Download my family's data
            </Button>
          </a>
          <Button
            variant="ghost"
            block
            onClick={async () => {
              await logout();
              window.location.assign("/");
            }}
          >
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
